import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Mail, Send, X } from 'lucide-react'
import { useSelector } from 'react-redux'
import roomMessagesService, { type RoomMessage } from '../../services/roomMessagesService'
import { webSocketService } from '../../services/websocket'
import { useTranslation } from '../../hooks/use-i18n'
import { formatChatMessageTime } from '../../utils/dateHelper'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import { useIsMobileLayout, useVisualViewport } from '../../hooks/useVisualViewport'
import type { RootState } from '../../store'

interface Props {
  roomId: string
  isOpen: boolean
  onClose: () => void
  onUnreadChange?: (count: number) => void
}

function messageKey(msg: RoomMessage): string {
  return msg.id ?? msg._id ?? ''
}

function isUserMessage(msg: RoomMessage): boolean {
  return msg.messageType === 'user' || Boolean(msg.authorId)
}

function upsertMessage(list: RoomMessage[], msg: RoomMessage): RoomMessage[] {
  const key = messageKey(msg)
  if (!key) return [...list, msg]
  if (list.some((m) => messageKey(m) === key)) return list
  return [...list, msg]
}

const RoomMessengerModal: React.FC<Props> = ({ roomId, isOpen, onClose, onUnreadChange }) => {
  const { t } = useTranslation()
  const { user } = useSelector((state: RootState) => state.auth)
  const userId = user?._id
  const authorName = user?.name ?? null
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  const listRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobileLayout()
  const viewport = useVisualViewport(isOpen && isMobile)

  useBodyScrollLock(isOpen && isMobile)

  const messagesQuery = useQuery({
    queryKey: ['room-messages', roomId],
    queryFn: () => roomMessagesService.list(roomId),
    enabled: isOpen && !!roomId,
    staleTime: 15 * 1000,
  })

  const messages = useMemo(() => {
    const seen = new Set<string>()
    return (messagesQuery.data ?? []).filter((m) => {
      const key = messageKey(m)
      if (!key || seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [messagesQuery.data])

  const sendMutation = useMutation({
    mutationFn: (content: string) => roomMessagesService.send(roomId, content),
    onSuccess: (msg) => {
      queryClient.setQueryData<RoomMessage[]>(['room-messages', roomId], (prev) =>
        upsertMessage(prev ?? [], msg),
      )
      setDraft('')
    },
  })

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior, block: 'end' })
    })
  }, [])

  useEffect(() => {
    if (!isOpen || !roomId) return
    const onNew = (msg: RoomMessage) => {
      if (msg.roomId !== roomId) return
      queryClient.setQueryData<RoomMessage[]>(['room-messages', roomId], (prev) =>
        upsertMessage(prev ?? [], msg),
      )
    }
    webSocketService.on('room:message:new', onNew)
    return () => {
      webSocketService.off('room:message:new', onNew)
    }
  }, [isOpen, roomId, queryClient])

  useEffect(() => {
    if (!isOpen || !roomId) return
    roomMessagesService.markAllRead(roomId).then(() => {
      onUnreadChange?.(0)
      queryClient.invalidateQueries({ queryKey: ['room-unread', roomId] })
    })
  }, [isOpen, roomId, messages.length, onUnreadChange, queryClient])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => scrollToBottom('auto'), 80)
    }
  }, [isOpen, messages.length, scrollToBottom])

  useEffect(() => {
    if (!isOpen || !isMobile) return
    scrollToBottom('auto')
  }, [isOpen, isMobile, viewport.height, scrollToBottom])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const panelClassName = `flex flex-col overflow-hidden min-h-0 shadow-2xl ${
    isMobile
      ? 'bg-background'
      : 'glass-card w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[85dvh] sm:max-h-[80dvh]'
  }`

  const panel = (
    <div
      className={panelClassName}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label={t('roomMessenger.title')}
    >
      <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Mail className="w-5 h-5 text-primary shrink-0" />
          <div className="min-w-0">
            <h2 className="font-semibold text-foreground text-sm sm:text-base truncate">
              {t('roomMessenger.title')}
            </h2>
            <p className="text-[11px] sm:text-xs text-muted-foreground truncate hidden xs:block">
              {t('roomMessenger.subtitle')}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-lg p-2 text-muted-foreground hover:text-foreground hover:bg-muted/30 touch-manipulation"
          aria-label={t('roomMessenger.close')}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div
        ref={listRef}
        className="flex-1 overflow-y-auto overscroll-contain px-3 sm:px-4 py-3 space-y-2.5 sm:space-y-3 min-h-0"
      >
        {messagesQuery.isLoading && (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}
        {!messagesQuery.isLoading && messages.length === 0 && (
          <p className="text-center text-sm text-muted-foreground py-8">{t('roomMessenger.empty')}</p>
        )}
        {messages.map((msg) => {
          const key = messageKey(msg)
          const isUser = isUserMessage(msg)
          const isOwn = isUser && String(msg.authorId) === String(userId)
          const displayName = isOwn ? authorName : msg.authorName
          const timeLabel = msg.createdAt ? formatChatMessageTime(String(msg.createdAt)) : ''

          if (!isUser) {
            return (
              <div key={key} className="flex justify-center">
                <div className="max-w-[92%] rounded-xl bg-muted/40 border border-border px-3 py-2 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                    {t('roomMessenger.weeklyUpdate')}
                  </p>
                  {timeLabel ? (
                    <p className="text-[10px] text-muted-foreground/80 mb-1">{timeLabel}</p>
                  ) : null}
                  <p className="text-sm text-foreground break-words">{msg.content}</p>
                </div>
              </div>
            )
          }
          return (
            <div key={key} className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
              {displayName ? (
                <p className="text-[11px] font-semibold text-sky-400 mb-0.5 px-1 truncate max-w-[85%]">
                  {displayName}
                </p>
              ) : null}
              <div
                className={`max-w-[88%] sm:max-w-[85%] rounded-2xl px-3 py-2 ${
                  isOwn ? 'bg-primary text-primary-foreground' : 'bg-muted/50 border border-border'
                }`}
              >
                <p className="text-sm sm:text-[15px] whitespace-pre-wrap break-words">{msg.content}</p>
              </div>
              {timeLabel ? (
                <p className="text-[10px] text-muted-foreground mt-0.5 px-1">{timeLabel}</p>
              ) : null}
            </div>
          )
        })}
        <div ref={bottomRef} className="h-px shrink-0" aria-hidden />
      </div>

      <form
        className="flex gap-2 px-3 sm:px-4 py-2.5 sm:py-3 border-t border-border shrink-0 bg-background/80 backdrop-blur-sm"
        style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}
        onSubmit={(e) => {
          e.preventDefault()
          const text = draft.trim()
          if (!text || sendMutation.isPending) return
          sendMutation.mutate(text)
        }}
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => scrollToBottom()}
          maxLength={500}
          placeholder={t('roomMessenger.placeholder')}
          enterKeyHint="send"
          autoComplete="off"
          autoCorrect="on"
          className="flex-1 min-w-0 rounded-xl border border-border bg-background px-3 py-2.5 text-base sm:text-sm text-foreground placeholder:text-muted-foreground touch-manipulation"
        />
        <button
          type="submit"
          disabled={!draft.trim() || sendMutation.isPending}
          className="shrink-0 rounded-xl bg-primary text-primary-foreground px-3 py-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center disabled:opacity-50 touch-manipulation"
          aria-label={t('roomMessenger.send')}
        >
          {sendMutation.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </form>
    </div>
  )

  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-[100]">
        <button
          type="button"
          className="absolute inset-0 bg-black/70 border-0 p-0 cursor-default"
          onClick={onClose}
          aria-label={t('roomMessenger.close')}
        />
        <div
          className="absolute z-[1] flex flex-col"
          style={{
            top: viewport.top,
            left: viewport.left,
            width: viewport.width,
            height: viewport.height,
          }}
        >
          {panel}
        </div>
      </div>,
      document.body,
    )
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={handleBackdropClick}
      role="presentation"
    >
      {panel}
    </div>,
    document.body,
  )
}

export default RoomMessengerModal
